import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, Search, X } from "lucide-react";
import { useContent } from "../context/ContentContext";
import { resolveMediaUrl } from "../lib/media";
import { postUrl } from "../lib/urls";
import SEO from "../components/SEO";
import ShareButtons from "../components/ShareButtons";
import CategorySidebar from "../components/CategorySidebar";

export default function Destinations() {
  const { posts, categories, tags, getCategory } = useContent();
  const [sp, setSp] = useSearchParams();
  const [category, setCategory] = useState(sp.get("category") || "all");
  const [tag, setTag] = useState(sp.get("tag") || "all");
  const [query, setQuery] = useState("");
  const [showAllTags, setShowAllTags] = useState(false);

  const filtered = useMemo(() => {
    return posts.filter((p) => {
      const c = category === "all" || p.categories?.includes(category);
      const t = tag === "all" || p.tags?.includes(tag);
      const q = !query || (p.title + p.excerpt + p.kicker).toLowerCase().includes(query.toLowerCase());
      return c && t && q;
    });
  }, [posts, category, tag, query]);

  const topTags = tags.slice(0, 18);
  const visibleTags = showAllTags ? tags : topTags;

  const reset = () => {
    setCategory("all"); setTag("all"); setQuery(""); setSp({});
  };
  const pickCategory = (slug) => {
    setCategory(slug);
    if (slug === "all") sp.delete("category"); else sp.set("category", slug);
    setSp(sp);
  };
  const pickTag = (slug) => {
    setTag(slug);
    if (slug === "all") sp.delete("tag"); else sp.set("tag", slug);
    setSp(sp);
  };

  const activeLabel =
    category !== "all" ? getCategory(category)?.name :
    tag !== "all" ? tags.find((t) => t.slug === tag)?.name : null;

  return (
    <main data-testid="destinations-page" className="bg-[#FDFBF7]">
      <SEO
        title="Destinations — The Atlas"
        description={`Browse ${posts.length} travel stories across ${categories.length} locations. Filter by country or topic — Italy, Scotland, Paris, Mauritius, Ecuador, Sri Lanka and more.`}
        path="/destinations"
      />
      <section className="pt-36 pb-14 lg:pt-44 lg:pb-20 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="kicker">The Atlas</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-[0.98] mt-5 max-w-4xl">
            Every place we've lingered,
            <br /><em className="italic">at least a month.</em>
          </h1>
          <div className="mt-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5">
            <p className="text-stone-600 max-w-2xl text-[15px] sm:text-base leading-relaxed">
              {categories.length} locations, {tags.length} tags, {posts.length} stories. Filter the archive by country or by the topics Ashwini keeps returning to.
            </p>
            <ShareButtons title="The Globezing Atlas" description="Browse travel stories by country or topic." />
          </div>
        </div>
      </section>

      <section className="py-10 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="lg:grid lg:grid-cols-[240px_1fr] lg:gap-14">
            {/* Sidebar: categories + tags */}
            <CategorySidebar
              categories={categories}
              activeSlug={category}
              onPick={pickCategory}
              totalPosts={posts.length}
              mode="filter"
              heading="Categories"
            >
              <p className="kicker text-stone-500 text-[10px] mb-4">Tags</p>
              <div className="flex flex-wrap gap-1.5">
                <TagChip active={tag === "all"} onClick={() => pickTag("all")} testid="filter-tag-all">All</TagChip>
                {visibleTags.map((t) => (
                  <TagChip key={t.slug} active={tag === t.slug} onClick={() => pickTag(t.slug)} testid={`filter-tag-${t.slug}`}>
                    {t.name}
                  </TagChip>
                ))}
                {tags.length > topTags.length && (
                  <button onClick={() => setShowAllTags((v) => !v)} data-testid="filter-tag-toggle" className="px-2.5 py-1 text-[11px] uppercase tracking-[0.15em] font-semibold text-[#9c4122] link-underline">
                    {showAllTags ? "Show fewer" : `+${tags.length - topTags.length} more`}
                  </button>
                )}
              </div>
            </CategorySidebar>

            <div>
              {/* Search row */}
              <div data-testid="destination-filters" className="mb-10 space-y-5 lg:sticky lg:top-28 lg:bg-[#FDFBF7] lg:pb-4 lg:z-20">
                <div className="flex items-center border-b border-stone-300 focus-within:border-[#9c4122]">
                  <Search size={16} className="text-stone-400" />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search stories"
                    data-testid="filter-search-input"
                    className="flex-1 bg-transparent outline-none px-3 py-2 text-sm"
                  />
                </div>

                {(category !== "all" || tag !== "all" || query) && (
                  <div className="flex items-center gap-3 pt-1">
                    <p className="text-xs text-stone-600">Active filter:&nbsp;<span className="font-semibold text-stone-900">{activeLabel || query}</span></p>
                    <button onClick={reset} data-testid="reset-filters" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-[#9c4122] font-semibold">
                      <X size={12} /> Reset
                    </button>
                  </div>
                )}

                <p className="kicker text-stone-500 pt-2 border-t border-stone-200">
                  <span data-testid="results-count" className="font-semibold text-stone-900">{filtered.length}</span> &nbsp;/&nbsp; {posts.length} stories
                </p>
              </div>

              {/* Grid */}
              {filtered.length === 0 ? (
                <div data-testid="no-results" className="py-24 text-center border border-dashed border-stone-300">
                  <p className="font-serif text-2xl text-stone-700">No stories match those filters.</p>
                  <button onClick={reset} className="mt-5 text-sm underline text-[#9c4122]">Reset filters</button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-14">
                  {filtered.map((post, i) => (
                    <motion.div key={post.slug} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.7, delay: (i % 6) * 0.08 }}>
                      <Link to={postUrl(post)} data-testid={`destination-card-${post.slug}`} className="group block">
                        <div className="relative aspect-[4/5] overflow-hidden bg-stone-200">
                          <img src={resolveMediaUrl(post.hero)} alt={post.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105" />
                          <div className="absolute top-4 left-4 flex items-center gap-2 text-white text-[10px] tracking-[0.25em] uppercase">
                            <span className="font-semibold">{post.categories?.[0] ? getCategory(post.categories[0])?.name : ""}</span>
                          </div>
                          <span className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-white/90 text-stone-900 flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all">
                            <ArrowUpRight size={16} />
                          </span>
                        </div>
                        <div className="mt-5">
                          <p className="kicker text-stone-500">{post.kicker} · {post.readTime} min</p>
                          <h3 className="font-serif text-2xl sm:text-[26px] font-normal leading-snug mt-2 text-stone-900 group-hover:text-[#9c4122] transition-colors">{post.title}</h3>
                          <p className="mt-2 text-stone-600 text-sm leading-relaxed line-clamp-2">{post.excerpt}</p>
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function TagChip({ active, onClick, testid, children }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`px-3 py-1.5 text-[12px] tracking-wide rounded-full border transition-all ${active ? "bg-stone-900 text-[#FDFBF7] border-stone-900" : "bg-transparent text-stone-700 border-stone-300 hover:border-stone-900"}`}>
      {children}
    </button>
  );
}
